import { createContext, useCallback, useEffect, useMemo, useState } from 'react';
import type { ReactNode } from 'react';

import { getProjectAlerts } from '../api/projects';
import type { ProjectAlertSummary } from '../api/projects';
import { PermissionCodes } from '../auth/permissions';
import { useAuth } from '../auth/useAuth';

/** Trạng thái cảnh báo dùng chung cho cả ứng dụng. */
export interface ProjectAlertState {
  /** Cảnh báo đã nạp, hoặc null khi chưa nạp xong hoặc người dùng không có quyền xem. */
  readonly summary: ProjectAlertSummary | null;

  /** Đang nạp. Dùng để không hiện con số 0 trong lúc chưa biết. */
  readonly isLoading: boolean;

  /**
   * Nạp lại cảnh báo.
   *
   * Khai dạng thuộc tính hàm chứ không dạng phương thức, cùng lý do như trạng thái xác thực: nơi
   * dùng lấy ra bằng phép tách đối tượng, và một phương thức bị tách khỏi đối tượng của nó sẽ mất
   * tham chiếu tới chính đối tượng đó.
   */
  readonly reload: () => void;
}

const ProjectAlertContext = createContext<ProjectAlertState | null>(null);

export { ProjectAlertContext };

/**
 * Cung cấp cảnh báo cho toàn ứng dụng.
 *
 * <b>Nạp một lần cho mỗi phiên, không phải một lần cho mỗi nơi hiển thị.</b> Chuông trên thanh tiêu
 * đề và danh sách ở trang chủ đọc cùng một con số; để hai nơi tự gọi riêng thì có lúc chuông báo
 * năm mà danh sách hiện bốn, và một con số tự mâu thuẫn thì người dùng thôi tin cả hai.
 *
 * <b>Chỉ gọi khi người dùng có quyền xem công việc.</b> Điểm cuối đòi quyền đó làm mức tối thiểu,
 * nên gọi cho người không có quyền chỉ tạo ra một lần bị từ chối ở mỗi lần đăng nhập — vừa vô ích
 * vừa làm nhật ký an ninh đầy những dòng từ chối không có ý nghĩa.
 *
 * <b>Không tự làm mới theo chu kỳ.</b> Cảnh báo ở đây trả lời câu <i>hôm nay có gì cần chú ý</i>,
 * và câu đó không đổi trong vài phút. Một bộ đếm giờ chạy nền sẽ gọi máy chủ suốt cả ngày kể cả khi
 * người dùng đang mở một thẻ khác. Thay vào đó, mỗi lần đăng nhập là một lần nạp, và ai muốn xem
 * lại ngay thì có nút tải lại.
 */
export function ProjectAlertProvider({ children }: { readonly children: ReactNode }): ReactNode {
  const { user, hasPermission } = useAuth();

  /*
    Lưu kèm người đã nạp, chứ không lưu mỗi phần dữ liệu. Nhờ vậy khi đổi người đăng nhập thì con số
    của người cũ KHÔNG hiện ra trong lúc chờ nạp cho người mới — và cảnh báo là thứ nói về công việc
    của một người cụ thể, nên hiện nhầm người là hiện sai.
  */
  const [loaded, setLoaded] = useState<{
    readonly userId: string;
    readonly summary: ProjectAlertSummary;
  } | null>(null);

  const [isLoading, setIsLoading] = useState(false);
  const [reloadToken, setReloadToken] = useState(0);

  const canView = hasPermission(PermissionCodes.ProjectTaskView);
  const userId = user?.userId ?? null;

  useEffect(() => {
    if (userId === null || !canView) {
      return;
    }

    let isCurrent = true;

    async function load(currentUserId: string): Promise<void> {
      setIsLoading(true);

      try {
        const summary = await getProjectAlerts();

        if (isCurrent) {
          setLoaded({ userId: currentUserId, summary });
        }
      } catch {
        /*
          Cảnh báo là thứ CÓ CŨNG ĐƯỢC: không lấy được thì để trống, tuyệt đối không chặn màn hình
          hay hiện một khung báo lỗi ở mọi trang. Người dùng vào hệ thống để làm việc, và một lời
          báo lỗi về phần phụ nằm chắn trước phần chính là cách chắc chắn làm họ bực.
        */
        if (isCurrent) {
          setLoaded(null);
        }
      } finally {
        if (isCurrent) {
          setIsLoading(false);
        }
      }
    }

    void load(userId);

    return () => {
      isCurrent = false;
    };
  }, [userId, canView, reloadToken]);

  /*
    Suy ra thay vì xóa trong hiệu ứng. Xóa trong hiệu ứng thì phải dựng lại giao diện thêm một lượt
    chỉ để về đúng thứ đã biết trước — còn suy ra thì lần dựng nào cũng đúng ngay từ đầu.
  */
  const summary = loaded !== null && loaded.userId === userId && canView ? loaded.summary : null;

  const reload = useCallback(() => {
    setReloadToken((token) => token + 1);
  }, []);

  const value = useMemo<ProjectAlertState>(
    () => ({ summary, isLoading, reload }),
    [summary, isLoading, reload],
  );

  return (
    <ProjectAlertContext.Provider value={value}>{children}</ProjectAlertContext.Provider>
  );
}
